import { Injectable } from '@angular/core';

/**
 * Globally disables browser autofill on all inputs, selects, and textareas
 * across the entire admin dashboard. Uses a MutationObserver so it works
 * with lazy-loaded routes and dynamically rendered components.
 *
 * Auth inputs (login/password) keep their explicit autocomplete attributes
 * because the selector skips elements that already have autocomplete set.
 */
@Injectable({ providedIn: 'root' })
export class NoAutofillService {

  private observer: MutationObserver | null = null;

  init() {
    // Apply to everything already in the DOM
    this.applyToSubtree(document.body);

    // Watch for any new elements added dynamically (route changes, modals, panels)
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            this.applyToSubtree(node);
          }
        });
      }
    });

    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  private applyToSubtree(root: Element) {
    const selector = [
      'input:not([autocomplete])',
      'select:not([autocomplete])',
      'textarea:not([autocomplete])',
    ].join(', ');

    // Also check the root element itself
    if (root.matches?.(selector)) {
      this.disableAutofill(root as HTMLElement);
    }

    root.querySelectorAll<HTMLElement>(selector).forEach((el) => {
      this.disableAutofill(el);
    });
  }

  private disableAutofill(el: HTMLElement) {
    el.setAttribute('autocomplete', 'off');
    el.setAttribute('autocorrect', 'off');
    el.setAttribute('autocapitalize', 'off');
    el.setAttribute('spellcheck', 'false');
  }
}
